// Drugs › Measured ΔFC, the view (features/dfc.ts reads the export): the condition and its people, how many cortical
// parcels it measured, its global change with the interval the source published, the legend for the cortex (a signed
// change: slate near zero, the tissue bare where nothing was measured), the parcels by the size of their change, and
// one parcel's own strength, edge count and strongest changed edges with the people behind them. The cortex takes the
// condition's strength through the viewport's live vertex layer with the change look (#705, design #711): range ±h
// with h the export's own strength limit, centre 0 so neither sign is muted, albedoFloor 0.4 so a measured zero reads
// as a faint slate. A still comparison: the view's preset is `still`, so the scene's clock and runs stand aside
// (app/scene.ts timelineOf, viewport/bridge.ts simCommands).

import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAtlas, type AtlasState } from '../app/store.ts';
import { getViewport } from '../viewport/host.ts';
import type { Command } from '../viewport/api.ts';
import { Button, Section, Segmented } from '../ui/primitives.tsx';
import { DfcMatrix } from './DfcMatrix.tsx';
import { useLoaded } from '../ui/hooks.ts';
import type { LiveViewport } from './stim.ts';
import { HD_CORTEX } from '../data/sims/crosswalk.ts';
import { conditionLabel, DFC_LAYER, drugLabel, dzText, intervalText, openDfc, parcelName, peopleText, useDfc, variantLabel, type Dfc, type LayerState } from './dfc.ts';
import './web.css';

export const dfcActive = (s: Pick<AtlasState, 'scenario' | 'preset'>): boolean => s.scenario === 'drugs' && s.preset === 'dfc';

/** The look the lead fixed for a signed change on the cortex (#705, #711; #2 comment 5963138650). */
const look = (h: number) => ({ colormap: 'change', range: [-h, h], centre: 0, albedoFloor: 0.4, blend: 'mix', albedo: 0.7, emission: 0 });

/** The condition's strength on the cortex: the layer once the pack is in (a load removes every layer), then a push
 *  per condition; taken off when the view closes. A viewport without live layers (the 2D preview) is said so. */
function useDfcLayer(active: boolean, data: Dfc | null) {
  const condition = useDfc((s) => s.condition);
  const setLayer = useDfc((s) => s.setLayer);
  const loadStatus = useAtlas((s) => s.load.status);
  const [made, setMade] = useState(0);
  useEffect(() => {
    if (!active || !data || loadStatus !== 'ready') return;
    let live = true;
    const h = data.limits.strength;
    void (async () => {
      const vp = (await getViewport()) as LiveViewport | null;
      if (!vp || !live) return;
      if (typeof vp.setVertexData !== 'function') {
        setLayer('none');
        return;
      }
      try {
        const reply = (await vp.send({ cmd: 'vertexLayer', id: DFC_LAYER, surface: 'cortex', space: 'fsLR-32k', kind: 'scalar', look: look(h) } as unknown as Command)) as
          | { vertices?: number; covered?: number; of?: number }
          | undefined;
        if (!live) return;
        setLayer('pushed', reply && typeof reply.covered === 'number' && typeof reply.of === 'number' ? { vertices: reply.vertices ?? 0, covered: reply.covered, of: reply.of } : null);
        setMade((n) => n + 1);
      } catch (err) {
        console.warn('[dfc] vertexLayer', err);
        if (live) setLayer('none');
      }
    })();
    return () => {
      live = false;
    };
  }, [active, data, loadStatus, setLayer]);
  useEffect(() => {
    if (!active || !data || !made) return;
    let live = true;
    void (async () => {
      const vp = (await getViewport()) as LiveViewport | null;
      if (!vp || !live || typeof vp.setVertexData !== 'function') return;
      const row = await data.vertexStrength(data.condition(condition).index);
      if (!live) return;
      try {
        vp.setVertexData(DFC_LAYER, row);
      } catch (err) {
        console.warn('[dfc] setVertexData', err);
        setLayer('none');
      }
    })();
    return () => {
      live = false;
    };
  }, [active, data, condition, made, setLayer]);
  useEffect(() => {
    if (!active) return;
    return () => {
      setMade(0);
      useDfc.getState().setLayer('off');
      void getViewport()?.then((vp) => vp.send({ cmd: 'vertexLayer', id: DFC_LAYER, remove: true } as unknown as Command).catch(() => undefined));
    };
  }, [active]);
}

/** The matrix sheet closes with the view (not in the layer effect's cleanup: StrictMode runs that once at mount too). */
function useCloseMatrixOffView(active: boolean) {
  useEffect(() => {
    if (!active) useDfc.getState().setMatrixOpen(false);
  }, [active]);
}

function layerNote(layer: LayerState, covered: { covered: number; of: number } | null): string {
  if (layer === 'pushed' && covered) return `On the cortex through its fsLR-32k map: ${covered.covered.toLocaleString()} of ${covered.of.toLocaleString()} displayed vertices.`;
  if (layer === 'none') return 'The 2D preview doesn’t draw the cortex strength; the numbers are the run’s.';
  return 'Each parcel’s mean change over its available edges, Δz, on one scale for every condition.';
}

function ConditionSection({ data }: { data: Dfc }) {
  const id = useDfc((s) => s.condition);
  const setCondition = useDfc((s) => s.setCondition);
  const layer = useDfc((s) => s.layer);
  const covered = useDfc((s) => s.covered);
  const c = data.condition(id);
  const drugs = [...new Set(data.conditions.map((x) => x.drug))];
  const variants = data.conditions.filter((x) => x.drug === c.drug);
  const counts = data.finiteCortical(c.index);
  const h = data.limits.strength;
  const st = c.statistics;
  const pickDrug = (d: string) => setCondition((data.conditions.find((x) => x.drug === d && x.role === 'primary') ?? data.conditions.find((x) => x.drug === d))?.id ?? id);
  const matrixOpen = useDfc((s) => s.matrixOpen);
  const setMatrixOpen = useDfc((s) => s.setMatrixOpen);
  const closeMatrix = useCallback(() => setMatrixOpen(false), [setMatrixOpen]);
  return (
    <Section
      title="Measured ΔFC"
      action={
        <Button variant="quiet" className="dfc-matrix-btn" aria-pressed={matrixOpen} onClick={() => setMatrixOpen(!matrixOpen)}>
          Matrix
        </Button>
      }
    >
      {matrixOpen ? createPortal(<DfcMatrix data={data} onClose={closeMatrix} />, document.body) : null}
      <Segmented size="sm" label="Drug" value={c.drug} options={drugs.map((d) => ({ value: d, label: drugLabel(d) }))} onChange={pickDrug} />
      <label className="dfc-variant">
        <span className="ba-type-small muted">Comparison</span>
        <select className="select dfc-select" value={c.id} aria-label="Comparison" onChange={(e) => setCondition(e.target.value)}>
          {variants.map((v) => (
            <option key={v.id} value={v.id}>{`${variantLabel(v)}${v.role === 'primary' ? ' · primary' : ''}`}</option>
          ))}
        </select>
      </label>
      <dl className="dfc-facts">
        <div>
          <dt>People</dt>
          <dd>{`${c.n_subjects}, ${c.sample.replace(/_/g, ' ')}; drug minus ${c.control}`}</dd>
        </div>
        <div>
          <dt>Cortical parcels</dt>
          <dd className="dfc-count">{`${counts.measured} of ${counts.of} measured`}</dd>
        </div>
        <div>
          <dt>Global Δz</dt>
          <dd>{`${dzText(st.mean_global_delta_fc_z, 3)} (95 % interval ${intervalText(st.global_delta_fc_95ci)})`}</dd>
        </div>
        <div>
          <dt>Data</dt>
          <dd>{`OpenNeuro ${c.dataset}`}</dd>
        </div>
      </dl>
      <div className="dfc-legend" role="img" aria-label={`Change in connectivity, −${h.toFixed(2)} to +${h.toFixed(2)} Δz; slate near zero, untinted where nothing was measured`}>
        <span className="dfc-strip" />
        <span className="dfc-ticks ba-type-caption">
          <span>{`−${h.toFixed(2)}`}</span>
          <span>0</span>
          <span>{`+${h.toFixed(2)}`}</span>
        </span>
        <span className="dfc-key ba-type-caption">Slate: near zero · untinted: no measurement</span>
      </div>
      <p className="dfc-note ba-type-caption muted">{layerNote(layer, covered)}</p>
    </Section>
  );
}

function ParcelSection({ data }: { data: Dfc }) {
  const id = useDfc((s) => s.condition);
  const parcel = useDfc((s) => s.parcel);
  const setParcel = useDfc((s) => s.setParcel);
  const c = data.condition(id).index;
  const ranked = data.ranked(c, 8);
  const node = parcel !== null ? (data.nodes[parcel] ?? null) : null;
  const measured = !!node && data.measured(c, node.index);
  const edges = useLoaded(() => (node && measured ? data.strongestEdges(c, node.index) : null), [data, c, node?.index, measured]);
  const cortex = data.nodes.filter((n) => n.component === 'cortex');
  const deep = data.nodes.filter((n) => n.component !== 'cortex');
  return (
    <Section title="Parcels">
      <ul className="dfc-ranked" aria-label="Largest changes">
        {ranked.map((r) => (
          <li key={r.node.index}>
            <button type="button" className={`dfc-parcel ${parcel === r.node.index ? 'on' : ''}`} aria-pressed={parcel === r.node.index} onClick={() => setParcel(r.node.index)}>
              <span className="dfc-parcel-name">{parcelName(r.node)}</span>
              <span className={`dfc-dz num ${r.dz < 0 ? 'neg' : 'pos'}`}>{dzText(r.dz)}</span>
            </button>
          </li>
        ))}
      </ul>
      <label className="dfc-variant">
        <span className="ba-type-small muted">Any parcel</span>
        <select className="select dfc-select" value={parcel ?? ''} aria-label="Parcel" onChange={(e) => setParcel(e.target.value === '' ? null : Number(e.target.value))}>
          <option value="">Choose a parcel</option>
          <optgroup label="Cortex (Schaefer-400)">
            {cortex.map((n) => (
              <option key={n.index} value={n.index}>
                {parcelName(n)}
              </option>
            ))}
          </optgroup>
          <optgroup label="Subcortex (Tian S2)">
            {deep.map((n) => (
              <option key={n.index} value={n.index}>
                {parcelName(n)}
              </option>
            ))}
          </optgroup>
        </select>
      </label>
      {node ? (
        <dl className="dfc-facts dfc-detail">
          <div>
            <dt>Parcel</dt>
            <dd>{`${parcelName(node)} · ${node.component}`}</dd>
          </div>
          <div>
            <dt>Change</dt>
            <dd className="dfc-measure">{measured ? `${dzText(data.strength(c)[node.index]!)} Δz over ${data.edges(c)[node.index]} edges` : 'No measurement'}</dd>
          </div>
          {measured ? (
            <div>
              <dt>Strongest</dt>
              <dd className="dfc-edges">{edges.value ? edges.value.map((e) => `${parcelName(e.node)} ${dzText(e.dz)} (${peopleText(e.people)})`).join(' · ') : edges.error ? 'The matrix didn’t load' : 'Reading the matrix…'}</dd>
            </div>
          ) : null}
        </dl>
      ) : null}
    </Section>
  );
}

/** The scene panel's sections for the view (LeftPanel mounts it; nothing when the view isn't on). */
export function DfcSections() {
  const active = useAtlas(dfcActive);
  const data = useDfc((s) => s.data);
  const setData = useDfc((s) => s.setData);
  useEffect(() => {
    if (!active || data !== undefined) return;
    void openDfc().then(setData, (err: unknown) => {
      console.warn('[dfc]', err);
      setData(null);
    });
  }, [active, data, setData]);
  useDfcLayer(active, data ?? null);
  useCloseMatrixOffView(active);
  if (!active) return null;
  if (data === undefined)
    return (
      <Section title="Measured ΔFC">
        <p className="dfc-note ba-type-small muted">Reading the export (pharma/measured-delta-fc-v1)…</p>
      </Section>
    );
  if (data === null)
    return (
      <Section title="Measured ΔFC">
        <p className="dfc-note ba-type-small muted">The measured ΔFC run (pharma/measured-delta-fc-v1) isn’t under sims/ on this machine.</p>
      </Section>
    );
  return (
    <>
      <ConditionSection data={data} />
      <ParcelSection data={data} />
    </>
  );
}

/** The Methods panel's section while the view is on: the export's own methods, the display rules and the scales. */
export function DfcMethods() {
  const data = useDfc((s) => s.data);
  const id = useDfc((s) => s.condition);
  const layer = useDfc((s) => s.layer);
  const covered = useDfc((s) => s.covered);
  if (!data) return null;
  const c = data.condition(id);
  const conn = data.conn;
  const ds = conn.datasets?.[c.dataset];
  return (
    <section className="methods-sim methods-dfc">
      <h3>{`Measured ΔFC · ${conditionLabel(c)}`}</h3>
      {data.header.methods ? <p>{data.header.methods}</p> : null}
      <p>
        {`Each parcel’s strength is the mean of its available off-diagonal changes (Δz), including subcortical neighbours. Missing cortical parcels show as bare tissue and a measured zero as a faint slate; NaN, or no one behind an edge, means missing, never zero. ${conn.statistics?.intervals ?? ''}`.trim()}
      </p>
      <p>{`A region picked on the cortex lists, in the Inspector, the Schaefer parcels inside it by their share of its vertices on the HD cortex (${HD_CORTEX}’s schaefer400 and dkt label layers), each with its change; the region’s own change is the vertex-weighted mean over its measured parcels.`}</p>
      {ds?.processing ? <p>{`${c.dataset}${ds.version ? ` v${ds.version}` : ''}: ${ds.processing}${ds.sensitivity ? ` ${ds.sensitivity}` : ''}`}</p> : null}
      {conn.comparability ? <p>{conn.comparability}</p> : null}
      {conn.interpretation ? <p>{conn.interpretation}</p> : null}
      <dl>
        <dt>Run</dt>
        <dd>
          <span className="mono">{data.header.id ?? 'measured-delta-fc-v1'}</span> <span className="muted">{`${data.header.model?.name ?? ''} ${data.header.model?.version ?? ''}`.trim()}</span>
        </dd>
        <dt>Scales</dt>
        <dd>{`Strength ±${data.limits.strength.toFixed(3)} Δz, matrix ±${data.limits.matrix.toFixed(3)} Δz: one scale for every condition`}</dd>
        <dt>On the cortex</dt>
        <dd>{layer === 'pushed' && covered ? `${covered.covered.toLocaleString()} of ${covered.of.toLocaleString()} displayed vertices, through the fsLR-32k map` : layer === 'none' ? 'Not drawn by this viewport' : 'Waiting for the viewport'}</dd>
        {c.source_group ? (
          <>
            <dt>Source</dt>
            <dd className="mono">{c.source_group}</dd>
          </>
        ) : null}
      </dl>
    </section>
  );
}
